/**
 * ตัวกรองแบบ "เลือกได้หลายอัน" — dropdown ติ๊กถูก (เช่น เลือก 3 แผนกพร้อมกัน).
 *
 * เลือกไว้ = แสดงชื่อที่เลือก (เกิน 2 อันย่อเป็น "เลือก N รายการ" กันปุ่มยืด),
 * ไม่เลือกเลย = ความหมายเดียวกับ "ทั้งหมด" (allLabel) เหมือน dropdown เดิม
 * เพื่อไม่ให้คนใช้ต้องเรียนรู้ใหม่.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export function MultiSelect({
  options,
  value,
  onChange,
  allLabel,
  label,
  disabled,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  /** ข้อความตอนไม่ได้เลือกอะไรเลย เช่น "ทุกแผนก" */
  allLabel: string;
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(opt: string) {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  }

  const summary =
    value.length === 0 ? allLabel
      : value.length <= 2 ? value.join(' · ')
      : `เลือก ${value.length} รายการ`;

  return (
    <div className="relative" ref={boxRef}>
      {label && <label className="label">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center justify-between gap-2 text-left disabled:opacity-50"
      >
        <span className={`truncate whitespace-nowrap ${value.length ? 'text-ink' : 'text-warm-gray'}`}>{summary}</span>
        <ChevronDown className="w-4 h-4 text-warm-gray shrink-0" />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full min-w-[220px] max-h-72 overflow-auto card p-1 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm hover:bg-gray-50 ${value.length === 0 ? 'text-pink-700 font-medium' : 'text-ink'}`}
          >
            <span className="w-4 shrink-0">{value.length === 0 && <Check className="w-4 h-4" />}</span>
            <span className="truncate">{allLabel}</span>
          </button>
          {options.length === 0 && (
            <p className="px-2.5 py-2 text-xs text-warm-gray">ยังไม่มีตัวเลือก</p>
          )}
          {options.map((opt) => {
            const on = value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm hover:bg-gray-50 ${on ? 'text-pink-700 font-medium' : 'text-ink'}`}
              >
                <span className="w-4 shrink-0">{on && <Check className="w-4 h-4" />}</span>
                <span className="truncate text-left">{opt}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
